import React, { useEffect, useMemo, useState } from "react";

import styles from "./UpdatesTabs.module.css";
import warningIcon from "../icons/warning.svg";
import infoIcon from '../icons/info.svg'
import closeIcon from "../icons/close.svg";
import backArrowIcon from "../icons/back-arrow.svg";

const toneLabelMap = {
  warning: "Alert",
  information: "Information",
  info: "Information",
  success: "Success",
  error: "Error",
};

const toneBackgroundMap = {
  warning: "#fff9eb",
  information: "#eff6ff",
  info: "#eff6ff",
  success: "#ecfdf3",
  error: "#fff1f2",
};

const toneBorderMap = {
  warning: "#fde8b2",
  information: "#bfdbfe",
  info: "#bfdbfe",
  success: "#bbf7d0",
  error: "#fecdd3",
};

const statusIconMap = {
  warning: warningIcon,
  information: infoIcon,
  info: infoIcon,
};

const MessageBox = ({
  messageObj,
  status = "warning",
  onClose,
  navigation,
}) => {
  if (messageObj?.component) {
    return (
      <div
        className={[
          styles.messageBox,
          styles[status],
          messageObj.messageBoxClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={styles.messageBody}>{messageObj.component}</div>
        <div className={styles.rightRail}>
          <button
            type="button"
            className={styles.closeButton}
            style={{ visibility: onClose ? "visible" : "hidden" }}
            onClick={onClose}
          >
            <img src={closeIcon} alt="close" />
          </button>
          {navigation && (
            <div className={styles.navigation}>
              <button
                type="button"
                className={styles.navButton}
                disabled={!navigation.canGoPrev}
                onClick={navigation.onPrev}
              >
                <img
                  src={backArrowIcon}
                  alt="previous"
                  className={styles.prevArrow}
                />
              </button>
              <span className={styles.navCount}>
                {navigation.current} / {navigation.total}
              </span>
              <button
                type="button"
                className={styles.navButton}
                disabled={!navigation.canGoNext}
                onClick={navigation.onNext}
              >
                <img
                  src={backArrowIcon}
                  alt="next"
                  className={styles.nextArrow}
                />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={[styles.messageBox, styles[status]].join(" ")}>
      <div className={[styles.messageBody, styles.messageSection].join(" ")}>
        <div className={styles.iconWrap}>
          <img
            src={messageObj?.messageIcon || statusIconMap[status] || warningIcon}
            alt="message"
            className={styles.warningIcon}
          />
        </div>
        <div className={styles.content}>
          {messageObj?.title && (
            <p className={styles.title}>{messageObj.title}</p>
          )}
          {messageObj?.text && (
            <p className={styles.message}>{messageObj.text}</p>
          )}
        </div>
      </div>
      <div className={styles.rightRail}>
        {onClose && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
          >
            <img src={closeIcon} alt="close" />
          </button>
        )}
        {navigation && (
          <div className={styles.navigation}>
            <button
              type="button"
              className={styles.navButton}
              disabled={!navigation.canGoPrev}
              onClick={navigation.onPrev}
            >
              <img
                src={backArrowIcon}
                alt="previous"
                className={styles.prevArrow}
              />
            </button>
            <span className={styles.navCount}>
              {navigation.current} / {navigation.total}
            </span>
            <button
              type="button"
              className={styles.navButton}
              disabled={!navigation.canGoNext}
              onClick={navigation.onNext}
            >
              <img
                src={backArrowIcon}
                alt="next"
                className={styles.nextArrow}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const buildTabHeader = (toneGroup, index, renderTabHeader) => {
  const label =
    toneGroup?.label ||
    toneLabelMap[toneGroup?.status] ||
    `Update ${index + 1}`;
  const toneCount = toneGroup?.messages?.length || 0;
  const defaultHeader = (
    <span
      className={
        toneGroup?.status === "information" || toneGroup?.status === "info"
          ? styles.informationTabText
          : styles.warningTabText
      }
    >
      {label} ({toneCount})
    </span>
  );
  return renderTabHeader
    ? renderTabHeader(toneGroup, index, defaultHeader)
    : defaultHeader;
};

const UpdatesTabs = ({
  messages = [],
  alerts = [],
  containerProps,
  renderTabHeader,
}) => {
  const storageKey = "updates-tabs-dismissed-message-ids";
  const [dismissedMessageIds, setDismissedMessageIds] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey)) || [];
    } catch (error) {
      return [];
    }
  });
  const [toneMessageIndex, setToneMessageIndex] = useState({});
  const [activeTone, setActiveTone] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(false);

  const messageList = messages.length ? messages : alerts;

  const toneGroups = useMemo(() => {
    const map = {};
    messageList.forEach((messageObj) => {
      if (!messageObj.id || dismissedMessageIds.includes(messageObj.id)) return;
      const status = messageObj.status || "warning";
      if (!map[status]) {
        map[status] = {
          status,
          label: messageObj.tabLabel || toneLabelMap[status] || status,
          messages: [],
        };
      }
      map[status].messages.push({ ...messageObj, __id: messageObj.id });
    });
    return Object.values(map).filter((group) => group.messages.length > 0);
  }, [messageList, dismissedMessageIds]);

  const hasVisibleMessages = toneGroups.length > 0;
  const activeToneValue =
    activeTone && toneGroups.some((g) => g.status === activeTone)
      ? activeTone
      : toneGroups[0]?.status;
  const currentToneGroup =
    toneGroups.find((g) => g.status === activeToneValue) || toneGroups[0];
  const showTabsHeader = toneGroups.length > 1;

  useEffect(() => {
    if (!hasVisibleMessages) {
      setVisible(false);
      return;
    }

    setCollapsed(false);
    const frameId = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frameId);
  }, [hasVisibleMessages]);

  if (!hasVisibleMessages) return null;

  const closeMessage = (messageObj) => {
    setDismissedMessageIds((prev) => {
      const nextDismissedIds = [...new Set([...prev, messageObj.__id])];
      sessionStorage.setItem(storageKey, JSON.stringify(nextDismissedIds));
      return nextDismissedIds;
    });
  };

  const renderToneView = (toneGroup) => {
    const currentIndex = toneMessageIndex[toneGroup.status] || 0;
    const safeIndex = Math.min(currentIndex, toneGroup.messages.length - 1);
    const currentMessage = toneGroup.messages[safeIndex];
    const canGoPrev = safeIndex > 0;
    const canGoNext = safeIndex < toneGroup.messages.length - 1;

    const setIndex = (nextIndex) => {
      setToneMessageIndex((prev) => ({
        ...prev,
        [toneGroup.status]: nextIndex,
      }));
    };

    const navigation = {
      current: safeIndex + 1,
      total: toneGroup.messages.length,
      canGoPrev,
      canGoNext,
      onPrev: () => canGoPrev && setIndex(safeIndex - 1),
      onNext: () => canGoNext && setIndex(safeIndex + 1),
    };

    return (
      <div className={styles.toneView}>
        <MessageBox
          messageObj={currentMessage}
          status={toneGroup.status}
          onClose={
            currentMessage.dismissible !== false
              ? () => closeMessage(currentMessage)
              : undefined
          }
          navigation={navigation}
        />
      </div>
    );
  };

  return (
    <div
      {...containerProps}
      className={[
        styles.container,
        !showTabsHeader && styles.singleToneContainer,
        collapsed && styles.collapsed,
        containerProps?.className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showTabsHeader && (
        <div
          className={styles.tabsHeaderRow}
          onClick={() => setCollapsed((prev) => !prev)}
          style={{
            "--active-status-border":
              toneBorderMap[currentToneGroup?.status] || "#e6eaf0",
          }}
        >
          <div className={styles.tabsHeaderButtons}>
            {toneGroups.map((toneGroup, index) => (
              <button
                key={`update-tab-${toneGroup.status}-${index}`}
                type="button"
                className={[
                  styles.headerTabButton,
                  activeToneValue === toneGroup.status &&
                    styles.activeHeaderTabButton,
                ].join(" ")}
                style={{
                  "--tab-underline-color":
                    toneGroup?.status === "information" ||
                    toneGroup?.status === "info"
                      ? "#1D4ED8"
                      : "#F59E0B",
                  "--tab-active-background":
                    toneBackgroundMap[toneGroup?.status] || "#ffffff",
                  "--tab-active-border":
                    toneBorderMap[toneGroup?.status] || "transparent",
                }}
                onClick={(event) => {
                  if (!collapsed) event.stopPropagation();
                  setActiveTone(toneGroup.status);
                }}
              >
                {buildTabHeader(toneGroup, index, renderTabHeader)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={(event) => {
              event.stopPropagation();
              setCollapsed((prev) => !prev);
            }}
          >
            {collapsed ? "EXPAND" : "COLLAPSE"}
            <img
              src={backArrowIcon}
              alt="toggle"
              className={[
                styles.collapseCaret,
                collapsed ? styles.collapseCaretDown : styles.collapseCaretUp,
              ].join(" ")}
            />
          </button>
        </div>
      )}
      <div
        className={[
          styles.contentArea,
          (!visible || collapsed) && styles.hidden,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={styles.contentWrap}>
          {currentToneGroup && renderToneView(currentToneGroup)}
        </div>
      </div>
    </div>
  );
};

export default UpdatesTabs;
